import React, { useState, useEffect, useRef } from "react";
import { Calendar, X, Clock, User, Image as ImageIcon, Check } from "lucide-react";
import { AgendaData } from "../../CreateQickEventV2/types";
import CustomTimePicker from "../../../../components/common/CustomTimePicker/CustomTimePicker";
import ImageCropper from "../../../../components/common/ImageCropper/ImageCropper";

interface NewAgendaModalV3Props {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (agenda: AgendaData) => void;
  editingAgenda?: AgendaData | null;
  isLoading?: boolean;
}

const NewAgendaModalV3: React.FC<NewAgendaModalV3Props> = ({
  isOpen,
  toggle,
  onSubmit,
  editingAgenda = null,
  isLoading = false,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startTime, setStartTime] = useState("09:00 AM");
  const [endTime, setEndTime] = useState("10:00 AM");
  const [hostName, setHostName] = useState("");

  const [hostImage, setHostImage] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [imageError, setImageError] = useState("");
  const previewUrlRef = useRef<string | null>(null);

  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [fileToCrop, setFileToCrop] = useState<File | null>(null);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (isOpen) {
      if (editingAgenda) {
        setTitle(editingAgenda.title || "");
        setDescription(editingAgenda.description || "");
        setStartTime(editingAgenda.startTime || "09:00 AM");
        setEndTime(editingAgenda.endTime || "10:00 AM");
        setHostName(editingAgenda.hostName || "");

        if (editingAgenda.hostImage instanceof File) {
          const url = URL.createObjectURL(editingAgenda.hostImage);
          previewUrlRef.current = url;
          setPreviewImage(url);
          setHostImage(editingAgenda.hostImage);
        } else if (editingAgenda.hostImageUrl) {
          setPreviewImage(editingAgenda.hostImageUrl);
        } else {
          setPreviewImage(null);
          setHostImage(null);
        }
      } else {
        setTitle("");
        setDescription("");
        setStartTime("09:00 AM");
        setEndTime("10:00 AM");
        setHostName("");
        setHostImage(null);
        setPreviewImage(null);
      }
      setValidationError("");
      setImageError("");
      setIsCropperOpen(false);
    }
  }, [editingAgenda, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setValidationError("Session Title is required.");
      return;
    }

    const agendaItem: AgendaData = {
      id: editingAgenda?.id || `agenda_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      title: title.trim(),
      description: description.trim(),
      agendaDate: editingAgenda?.agendaDate || "",
      startTime,
      endTime,
      hostName: hostName.trim(),
      hostImage,
      hostImageKey: hostImage?.name || editingAgenda?.hostImageKey || "",
      hostImageUrl: previewImage,
      sortOrder: editingAgenda?.sortOrder || 0,
      isActive: editingAgenda?.isActive ?? true,
    };

    onSubmit(agendaItem);
    toggle();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setImageError("");
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setImageError("Max file size is 2 MB.");
      return;
    }

    setFileToCrop(file);
    setIsCropperOpen(true);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 9999,
        background: "rgba(0, 0, 0, 0.75)",
        backdropFilter: "blur(12px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={toggle}
    >
      <div
        className="glass-panel"
        style={{
          width: "100%",
          maxWidth: 540,
          background: "rgba(18, 20, 29, 0.94)",
          border: "1px solid rgba(255, 255, 255, 0.12)",
          borderRadius: 22,
          padding: 24,
          boxShadow: "0 24px 60px rgba(0,0,0,0.8)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255,255,255,0.08)", paddingBottom: 16, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Calendar size={18} color="#EE3F7C" />
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#fff" }}>
              {editingAgenda ? "Edit Agenda Item" : "Add New Agenda Item"}
            </h3>
          </div>
          <button
            type="button"
            onClick={toggle}
            style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", cursor: "pointer", padding: 4 }}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Validation Alert */}
          {validationError && (
            <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(255,93,115,0.15)", border: "1px solid #FF5D73", color: "#FF5D73", fontSize: 12, fontWeight: 600 }}>
              {validationError}
            </div>
          )}

          {/* Session Title */}
          <div>
            <label className="v3-label">
              Session Title <span style={{ color: "#EE3F7C" }}>*</span>
            </label>
            <input
              type="text"
              className="v3-input"
              placeholder="e.g. Keynote Speech & Product Unveiling"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (validationError) setValidationError("");
              }}
            />
          </div>

          {/* Time Block (Start & End) */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <label className="v3-label">Start Time <span style={{ color: "#EE3F7C" }}>*</span></label>
              <CustomTimePicker value={startTime} onChange={setStartTime} placeholder="09:00 AM" />
            </div>
            <div>
              <label className="v3-label">End Time</label>
              <CustomTimePicker value={endTime} onChange={setEndTime} placeholder="10:00 AM" />
            </div>
          </div>

          {/* Host / Speaker Name */}
          <div>
            <label className="v3-label">Host / Speaker Name</label>
            <input
              type="text"
              className="v3-input"
              placeholder="e.g. Sarah Jenkins"
              value={hostName}
              onChange={(e) => setHostName(e.target.value)}
            />
          </div>

          {/* Session Description */}
          <div>
            <label className="v3-label">Session Description</label>
            <textarea
              className="v3-input"
              rows={3}
              placeholder="Brief outline of the agenda topic or discussion points..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Host Image Upload with Cropper */}
          <div>
            <label className="v3-label">Host Image (Optional)</label>
            {previewImage && (
              <div style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 12 }}>
                <img
                  src={previewImage}
                  alt="Host Preview"
                  style={{ width: 64, height: 64, borderRadius: 10, objectFit: "cover", border: "1px solid rgba(255,255,255,0.15)" }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setPreviewImage(null);
                    setHostImage(null);
                  }}
                  style={{ background: "rgba(255,93,115,0.15)", border: "1px solid rgba(255,93,115,0.3)", color: "#FF5D73", borderRadius: 8, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}
                >
                  Remove Image
                </button>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              className="v3-input"
              style={{ fontSize: 11 }}
              onChange={handleFileChange}
            />
            {imageError && <div style={{ fontSize: 11, color: "#FF5D73", marginTop: 4 }}>{imageError}</div>}
          </div>

          {/* Actions */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 16, marginTop: 10 }}>
            <button
              type="button"
              className="v3-btn-secondary"
              onClick={toggle}
              disabled={isLoading}
              style={{ padding: "8px 18px", fontSize: 12 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="v3-btn-primary"
              disabled={isLoading}
              style={{ padding: "8px 22px", fontSize: 12 }}
            >
              {editingAgenda ? "Update Session" : "Save Session"}
            </button>
          </div>
        </form>

        {/* Cropper Overlay */}
        {isCropperOpen && fileToCrop && (
          <ImageCropper
            imageFile={fileToCrop}
            aspect={1}
            onCropComplete={(croppedFile) => {
              setHostImage(croppedFile);
              const url = URL.createObjectURL(croppedFile);
              if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
              previewUrlRef.current = url;
              setPreviewImage(url);
              setIsCropperOpen(false);
            }}
            onCancel={() => setIsCropperOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default NewAgendaModalV3;
